import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isAiConversationType } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { AIDraftStatus } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { InsertIntoTextIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InsertIntoTextIcon'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import {
  ChatRightSideBar,
  ChatSideBarSelection,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/chatRightSideBar/ChatRightSideBar'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import MessengerSideBarContent from './MessengerSideBarContent'
import { mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import { useAppDispatch } from '../../../../data/store'
import { Template } from '../../../documents/data/templatesApi'
import { InsertMessageOptions } from '../../BaseChat'
import { useSelectedClientAIDraftData } from '../../data/hooks/useSelectedClientAIDraftData'
import { getEssentialsArticleUrl } from '../../data/messagesSelectors'
import { setInsertEssentialsArticleForClientId } from '../../data/messagesSlice'
import { getAiConversationMixpanelProperties } from '../../data/mixpanelUtils'

const ChatRightSideBarContainer = styled(ChatRightSideBar)(({ theme }) => ({
  borderLeftWidth: '1px',
  borderLeftColor: theme.colors.borderDefault,
}))

type MessengerSideBarProps = {
  selectedClientId: string | null
  isDetailsPanelOpen: boolean
  setIsDetailsPanelOpen: (value: boolean) => void
  insertMessage: (message: string, opts?: InsertMessageOptions) => void
  insertDirectBookingLink: () => void
  insertEssentialsArticleChip: () => void
  onOpenAssignment: (assignmentId: string) => void
  onOpenNotes: (noteId: string) => void
  selectedConversationType?: CONVERSATION_TYPE
  isEssentialsDisabled?: boolean
  pendingSideBarSelection?: ChatSideBarSelection | null
  onPendingSideBarSelectionOpened?: () => void
  onAttachDocument: (template: Template) => void
}

const MessengerSideBar: React.FC<MessengerSideBarProps> = ({
  selectedClientId,
  isDetailsPanelOpen,
  setIsDetailsPanelOpen,
  insertMessage,
  insertDirectBookingLink,
  insertEssentialsArticleChip,
  onOpenAssignment,
  onOpenNotes,
  selectedConversationType = CONVERSATION_TYPE.PROVIDER,
  isEssentialsDisabled = false,
  pendingSideBarSelection,
  onPendingSideBarSelectionOpened,
  onAttachDocument,
}) => {
  const dispatch = useAppDispatch()
  const { colors } = useTheme()
  const {
    shouldShowMessengerAIDrafts,
    shouldShowPostSessionAIDrafts,
    showWellnessCenterEssentials,
    showTemplatesButtonInMessageCenter,
    isDocumentMessageEnabled,
  } = useFlags()
  const [selectedItem, setSelectedItem] = useState(ChatSideBarSelection.CLIENT_DETAILS)
  const prevConversationTypeRef = useRef<CONVERSATION_TYPE | null>(null)
  const prevSelectedClientIdRef = useRef(selectedClientId)
  const isAiConversationSelected = isAiConversationType(selectedConversationType)

  const { data: aiDraft } = useSelectedClientAIDraftData()

  useEffect(() => {
    const conversationTypeChanged = prevConversationTypeRef.current !== selectedConversationType
    const clientChanged = prevSelectedClientIdRef.current !== selectedClientId

    if (aiDraft?.patient_id === selectedClientId && aiDraft?.status === AIDraftStatus.COMPLETE) {
      setSelectedItem(ChatSideBarSelection.SMART_SUGGESTIONS)
      setIsDetailsPanelOpen(true)
    } else if (conversationTypeChanged && isAiConversationSelected) {
      setSelectedItem(ChatSideBarSelection.SUMMARIES)
      track({
        event: mixpanelEvents.OPEN_AI_CHAT_SUMMARIES_SIDE_PANEL,
        properties: getAiConversationMixpanelProperties(selectedConversationType),
      })
    } else if (
      !isAiConversationSelected &&
      (clientChanged ||
        (prevConversationTypeRef.current != null && isAiConversationType(prevConversationTypeRef.current)))
    ) {
      setSelectedItem(ChatSideBarSelection.CLIENT_DETAILS)
    }

    prevConversationTypeRef.current = selectedConversationType
    prevSelectedClientIdRef.current = selectedClientId
  }, [
    aiDraft?.patient_id,
    aiDraft?.status,
    selectedClientId,
    setIsDetailsPanelOpen,
    selectedConversationType,
    isAiConversationSelected,
  ])

  useEffect(() => {
    if (pendingSideBarSelection) {
      setSelectedItem(pendingSideBarSelection)
      onPendingSideBarSelectionOpened?.()
    }
  }, [pendingSideBarSelection, onPendingSideBarSelectionOpened])

  const onCloseDetailsPanel = () => {
    switch (selectedItem) {
      case ChatSideBarSelection.SMART_SUGGESTIONS:
        track({
          event: mixpanelEvents.CLOSE_SMART_SUGGESTION_SIDE_PANEL,
          properties: getAiConversationMixpanelProperties(selectedConversationType),
        })
        break
      case ChatSideBarSelection.CLIENT_DETAILS:
        track({
          event: mixpanelEvents.MESSAGE_CENTER_COLLAPSE_CLIENT_DETAILS,
          properties: getAiConversationMixpanelProperties(selectedConversationType),
        })
        break
      case ChatSideBarSelection.TEMPLATES:
        track({
          event: mixpanelEvents.CLOSE_MESSAGE_TEMPLATES_SIDE_PANEL,
          properties: getAiConversationMixpanelProperties(selectedConversationType),
        })
        break
    }
    setIsDetailsPanelOpen(false)
  }

  const itemsToShow = useMemo(() => {
    const items = [ChatSideBarSelection.CLIENT_DETAILS]
    if (showTemplatesButtonInMessageCenter) {
      items.push(ChatSideBarSelection.TEMPLATES)
    }
    if (shouldShowMessengerAIDrafts || shouldShowPostSessionAIDrafts) {
      items.push(ChatSideBarSelection.SMART_SUGGESTIONS)
    }
    if (isAiConversationSelected) {
      items.push(ChatSideBarSelection.SUMMARIES)
    }
    if (showWellnessCenterEssentials && !isEssentialsDisabled) {
      items.push(ChatSideBarSelection.WELLNESS_LIBRARY_ESSENTIALS)
    }
    if (isDocumentMessageEnabled) {
      items.push(ChatSideBarSelection.DOCUMENT_LIBRARY)
    }
    return items
  }, [
    shouldShowMessengerAIDrafts,
    shouldShowPostSessionAIDrafts,
    showTemplatesButtonInMessageCenter,
    isAiConversationSelected,
    showWellnessCenterEssentials,
    isEssentialsDisabled,
    isDocumentMessageEnabled,
  ])

  const onItemSelected = useCallback(
    (item: ChatSideBarSelection) => {
      if (item === ChatSideBarSelection.SMART_SUGGESTIONS) {
        track({
          event: mixpanelEvents.OPEN_SMART_SUGGESTION_SIDE_PANEL,
          properties: {
            gen_ai_submission_tracker_id: aiDraft?.id,
            prompt_type: aiDraft?.prompt_type,
            ...getAiConversationMixpanelProperties(selectedConversationType),
          },
        })
      }
      if (item === ChatSideBarSelection.TEMPLATES) {
        track({
          event: mixpanelEvents.OPEN_TEMPLATES_SIDE_PANEL,
          properties: getAiConversationMixpanelProperties(selectedConversationType),
        })
      }
      if (item === ChatSideBarSelection.SUMMARIES) {
        track({
          event: mixpanelEvents.OPEN_AI_CHAT_SUMMARIES_SIDE_PANEL,
          properties: getAiConversationMixpanelProperties(selectedConversationType),
        })
      }
      if (item === ChatSideBarSelection.WELLNESS_LIBRARY_ESSENTIALS) {
        track({
          event: mixpanelEvents.VIEW_ESSENTIALS_CONTENT,
          properties: {
            clientId: selectedClientId,
            ...getAiConversationMixpanelProperties(selectedConversationType),
          },
        })
      }
      setSelectedItem(item)
    },
    [aiDraft?.id, aiDraft?.prompt_type, selectedClientId, selectedConversationType],
  )

  const essentialsLinkUrl = useSelector(getEssentialsArticleUrl)

  const insertIntoMessageButton = useMemo(
    () => (
      <SecondaryButton
        leftIcon={<InsertIntoTextIcon color={colors.linkDefault} />}
        testID={tID('MessengerTemplatesSideBar-insertEssentialsArticleIntoMessageButton')}
        fullWidth
        text={
          <FormattedMessage
            defaultMessage='Insert into message'
            description='Button to insert wellness library token into message'
          />
        }
        onPress={() => {
          track({
            event: mixpanelEvents.INSERT_WELLNESS_LIBRARY_ARTICLE_INTO_MESSAGE,
            properties: getAiConversationMixpanelProperties(selectedConversationType),
            details: {
              articleUrl: essentialsLinkUrl,
              clientId: selectedClientId,
            },
          })
          dispatch(setInsertEssentialsArticleForClientId(selectedClientId))
        }}
      />
    ),
    [colors.linkDefault, dispatch, essentialsLinkUrl, selectedClientId, selectedConversationType],
  )

  return (
    <ChatRightSideBarContainer
      hasSelectedClient={selectedClientId != null}
      isOpen={isDetailsPanelOpen}
      onClose={onCloseDetailsPanel}
      itemsToShow={itemsToShow}
      selectedItem={selectedItem}
      onItemSelected={onItemSelected}
      insertIntoMessageButton={insertIntoMessageButton}
      showInsertIntoMessageButton={!!essentialsLinkUrl}
    >
      <MessengerSideBarContent
        selectedItem={selectedItem}
        conversationType={selectedConversationType}
        insertMessage={insertMessage}
        insertDirectBookingLink={insertDirectBookingLink}
        insertEssentialsArticleChip={insertEssentialsArticleChip}
        onOpenAssignment={onOpenAssignment}
        onOpenNotes={onOpenNotes}
        onAttachDocument={onAttachDocument}
      />
    </ChatRightSideBarContainer>
  )
}

export default MessengerSideBar
